'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import {
  ArrowRight,
  Baby,
  Calendar,
  CarFront,
  ChevronDown,
  Fuel,
  MapPin,
  MessageCircle,
  Phone,
  Search,
  ShieldCheck,
  Sparkles,
  Users,
  Wrench,
} from 'lucide-react';

import { NewsletterSection } from '@/components/site/NewsletterSection';
import { TestimonialsSection } from '@/components/site/TestimonialsSection';
import { CarIllustration } from '@/components/site/CarIllustrations';
import { useCurrency } from '@/components/site/CurrencyContext';
import { usePublicBrand } from '@/components/site/PublicBrandProvider';
import { FAQS, RENTAL_LOCATIONS } from '@/lib/data';
import { useLocale } from '@/lib/i18n';
import { sectionEnabled } from '@/lib/storefront/designs';
import type { Vehicle } from '@/types';
import { type HomeDesignProps } from './home-props';

const HERO_BACKGROUND =  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=1920&q=80';
const PACKAGE_FALLBACKS = [
  'https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1533105079780-92b9be482077?auto=format&fit=crop&w=900&q=80',
  'https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=900&q=80',
];

const SAFETY_ITEMS = [
  { icon: ShieldCheck, title: 'rt.safety.1t', description: 'rt.safety.1d' },
  { icon: Wrench, title: 'rt.safety.2t', description: 'rt.safety.2d' },
  { icon: Baby, title: 'rt.safety.3t', description: 'rt.safety.3d' },
  { icon: Fuel, title: 'rt.safety.4t', description: 'rt.safety.4d' },
  { icon: Sparkles, title: 'rt.safety.5t', description: 'rt.safety.5d' },
];

/**
 * Travel-oriented design: photo hero with a full booking widget, fleet
 * categories, a safety strip, travel packages, a home page FAQ and an app band.
 */
export function RoadtripHome(props: HomeDesignProps) {
  const { vehicles, storefront, appearance } = props;

  return (
    <>
      <RoadtripHero {...props} />

      {sectionEnabled(appearance, 'fleetCategories') && <FleetCategories {...props} />}
      {sectionEnabled(appearance, 'safetyStrip') && <SafetyStrip />}
      {sectionEnabled(appearance, 'packages') && <TravelPackages {...props} />}
      {sectionEnabled(appearance, 'testimonials') && (
        <TestimonialsSection testimonials={storefront?.testimonials ?? []} />
      )}
      {sectionEnabled(appearance, 'faq') && <HomeFaq {...props} />}
      {sectionEnabled(appearance, 'appBand') && <AppBand />}
      {sectionEnabled(appearance, 'newsletter') && <NewsletterSection />}
    </>
  );
}

function RoadtripHero({ storefront, onSearch }: HomeDesignProps) {
  const { t } = useLocale();
  const [tab, setTab] = useState(0);
  const tabs = [t('rt.tab1'), t('rt.tab2'), t('rt.tab3')];
  const heroImage = storefront?.hero.imageUrl ?? '';
  const usableHeroImage = heroImage.startsWith('/') || /^https?:\/\//.test(heroImage);

  return (
    <section className="relative w-full overflow-hidden bg-slate-950">
      <div className="absolute inset-0">
        <Image
          src={usableHeroImage ? heroImage : HERO_BACKGROUND}
          alt=""
          fill
          priority
          className="object-cover object-center brightness-[0.55]"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/30" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-40 pt-16 sm:px-8 lg:pt-24">
        <div className="max-w-2xl">
          <h1 className="text-[30px] font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[56px]">
            {storefront?.hero.headline || (
              <>
                {t('rt.heroTitleA')}{' '}
                <span className="font-light italic text-[var(--color-primary)]">{t('rt.heroTitleB')}</span>
              </>
            )}
          </h1>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-200 sm:text-base">
            {storefront?.hero.description || t('rt.sub')}
          </p>
        </div>
      </div>

      <div className="relative z-20 mx-auto -mt-32 max-w-6xl px-4 sm:px-8">
        <RoadtripBookingWidget tab={tab} tabs={tabs} onTab={setTab} onSearch={onSearch} />
      </div>
    </section>
  );
}

/** The wide multi-field booking panel that sits over the hero photo. */
function RoadtripBookingWidget({
  tab,
  tabs,
  onTab,
  onSearch,
}: {
  tab: number;
  tabs: string[];
  onTab: (index: number) => void;
  onSearch: HomeDesignProps['onSearch'];
}) {
  const { t } = useLocale();
  const [pickupLocation, setPickupLocation] = useState(
    RENTAL_LOCATIONS[0] ? `${RENTAL_LOCATIONS[0].name} (${RENTAL_LOCATIONS[0].code})` : 'Agadir',
  );
  const [returnLocation, setReturnLocation] = useState(pickupLocation);
  const [pickupDate, setPickupDate] = useState('2026-10-18');
  const [returnDate, setReturnDate] = useState('2026-10-22');
  const [driverAge, setDriverAge] = useState('30');
  const [passengers, setPassengers] = useState('4');

  const fieldCls = 'rounded-2xl border border-slate-200/90 bg-slate-50 px-4 py-3';
  const labelCls = 'mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500';
  const controlCls =
    'w-full cursor-pointer bg-transparent text-xs font-semibold text-slate-900 outline-none sm:text-sm';

  return (
    <div className="rounded-[2rem] border border-slate-200/70 bg-white p-4 shadow-2xl shadow-slate-950/20 sm:p-6">
      <div className="flex flex-wrap gap-2">
        {tabs.map((label, index) => (
          <button
            key={label}
            type="button"
            onClick={() => onTab(index)}
            className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${
              tab === index
                ? 'bg-[var(--color-primary)] text-white shadow-sm'
                : 'text-slate-500 hover:bg-slate-100'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSearch({
            location: pickupLocation,
            pickupDate,
            pickupTime: '10:00 AM',
            dropoffDate: returnDate,
            dropoffTime: '10:00 AM',
            rentalType: tab === 0 ? 'daily' : tab === 1 ? 'longterm' : 'corporate',
          });
        }}
        className="mt-4 grid gap-3 lg:grid-cols-4"
      >
        <label className={fieldCls}>
          <span className={labelCls}>{t('rt.pickupLoc')}</span>
          <span className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
            <select className={controlCls} value={pickupLocation} onChange={(event) => setPickupLocation(event.target.value)}>
              {RENTAL_LOCATIONS.map((location) => (
                <option key={location.id} value={`${location.name} (${location.code})`}>
                  {location.name} ({location.code})
                </option>
              ))}
            </select>
          </span>
        </label>

        <label className={fieldCls}>
          <span className={labelCls}>{t('rt.returnLoc')}</span>
          <span className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
            <select className={controlCls} value={returnLocation} onChange={(event) => setReturnLocation(event.target.value)}>
              {RENTAL_LOCATIONS.map((location) => (
                <option key={location.id} value={`${location.name} (${location.code})`}>
                  {location.name} ({location.code})
                </option>
              ))}
            </select>
          </span>
        </label>

        <label className={fieldCls}>
          <span className={labelCls}>{t('search.pickupDate')}</span>
          <span className="flex items-center gap-2">
            <Calendar className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
            <input type="date" className={controlCls} value={pickupDate} onChange={(event) => setPickupDate(event.target.value)} />
          </span>
        </label>

        <label className={fieldCls}>
          <span className={labelCls}>{t('search.dropDate')}</span>
          <span className="flex items-center gap-2">
            <Calendar className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
            <input type="date" className={controlCls} value={returnDate} onChange={(event) => setReturnDate(event.target.value)} />
          </span>
        </label>

        <label className={fieldCls}>
          <span className={labelCls}>{t('rt.driverAge')}</span>
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
            <select className={controlCls} value={driverAge} onChange={(event) => setDriverAge(event.target.value)}>
              {['21', '25', '30', '40', '50', '60', '70'].map((age) => (
                <option key={age} value={age}>
                  {age}
                </option>
              ))}
            </select>
          </span>
        </label>

        <label className={fieldCls}>
          <span className={labelCls}>{t('rt.passengers')}</span>
          <span className="flex items-center gap-2">
            <CarFront className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
            <select className={controlCls} value={passengers} onChange={(event) => setPassengers(event.target.value)}>
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
          </span>
        </label>

        <button
          type="submit"
          className="inline-flex h-full min-h-[58px] items-center justify-center gap-2 rounded-2xl bg-[var(--color-primary)] px-6 text-xs font-bold uppercase tracking-wider text-white shadow-md transition-all hover:bg-[var(--color-primary-dark)] active:scale-[0.98] lg:col-span-2"
        >
          <Search className="h-4 w-4" />
          <span>{t('rt.search')}</span>
        </button>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-slate-100 pt-4">
        {['rt.trust1', 'rt.trust2', 'rt.trust3'].map((key) => (
          <span key={key} className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-[var(--color-primary)]" />
            {t(key)}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Categories derived from the agency's own fleet. */
function FleetCategories({
  vehicles,
  onViewAll,
}: HomeDesignProps) {
  const { t } = useLocale();
  const { fmt } = useCurrency();
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  const groups = new Map<string, Vehicle[]>();
  for (const vehicle of vehicles) {
    const bucket = groups.get(vehicle.category) ?? [];
    bucket.push(vehicle);
    groups.set(vehicle.category, bucket);
  }
  if (groups.size === 0) {
    return null;
  }

  return (
    <section className="bg-white py-14 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="mb-8 text-center">
          <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
            {t('rt.fleet.kicker')}
          </span>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t('rt.fleet.title')}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">{t('rt.fleet.sub')}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...groups.entries()].slice(0, 8).map(([category, list]) => {
            const lead = list.find((vehicle) => vehicle.imageUrl && !imageErrors[vehicle.id]) ?? list[0];
            const from = Math.min(...list.map((vehicle) => vehicle.pricePerDay));
            const showPhoto = Boolean(lead.imageUrl) && !imageErrors[lead.id];
            return (
              <article
                key={category}
                className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-44 w-full bg-slate-100">
                  {showPhoto ? (
                    <Image
                      src={lead.imageUrl ?? ''}
                      alt={category}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      referrerPolicy="no-referrer"
                      onError={() => setImageErrors((current) => ({ ...current, [lead.id]: true }))}
                    />
                  ) : (
                    <CarIllustration
                      type={lead.imageType}
                      className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="flex grow flex-col p-5">
                  <h3 className="text-base font-extrabold tracking-tight text-slate-900">{category}</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {list.length} {t('rt.fleet.cars')}
                  </p>
                  <p className="mt-3 text-xs font-semibold text-slate-500">
                    {t('rt.fleet.from')}{' '}
                    <span className="text-lg font-black tracking-tight text-[var(--color-primary)]">
                      {fmt(from)}
                    </span>
                    <span className="text-slate-400"> / day</span>
                  </p>
                  <button
                    type="button"
                    onClick={onViewAll}
                    className="mt-5 inline-flex items-center justify-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-800 transition-colors hover:border-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white"
                  >
                    {t('rt.fleet.view')}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SafetyStrip() {
  const { t } = useLocale();
  return (
    <section className="bg-slate-50 py-12 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="mb-8 text-center">
          <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
            {t('rt.safety.kicker')}
          </span>
          <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl">
            {t('rt.safety.title')}
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-5">
          {SAFETY_ITEMS.map((item) => (
            <div key={item.title} className="flex flex-col items-center text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-[var(--color-primary)] shadow-xs">
                <item.icon className="h-5 w-5" />
              </span>
              <span className="mt-3 block text-xs font-bold text-slate-900">{t(item.title)}</span>
              <span className="mt-1 block text-[11px] leading-relaxed text-slate-500">
                {t(item.description)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Multi-day offers the agency writes in the console's Website tab. */
function TravelPackages({ storefront, onBookAny }: HomeDesignProps) {
  const { t } = useLocale();
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});
  const packages = (storefront?.packages ?? []).filter((item) => item.title.trim() !== '');
  if (packages.length === 0) {
    return null;
  }

  return (
    <section className="bg-white py-14 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="mb-8 text-center">
          <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
            {t('rt.pack.kicker')}
          </span>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t('rt.pack.title')}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">{t('rt.pack.sub')}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {packages.slice(0, 6).map((item, index) => {
            const fallback = PACKAGE_FALLBACKS[index % PACKAGE_FALLBACKS.length];
            const source = item.imageUrl.startsWith('/') || item.imageUrl.startsWith('http')
              ? item.imageUrl
              : fallback;
            return (
              <article
                key={index}
                className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="relative h-48 w-full bg-slate-100">
                  {imageErrors[index] ? (
                    <span className="flex h-full w-full items-center justify-center text-slate-300">
                      <CarFront className="h-10 w-10" />
                    </span>
                  ) : (
                    <Image
                      src={source}
                      alt={item.title}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      referrerPolicy="no-referrer"
                      onError={() => setImageErrors((current) => ({ ...current, [index]: true }))}
                    />
                  )}
                  {item.duration ? (
                    <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-[11px] font-bold text-slate-900 shadow-sm">
                      {item.duration}
                    </span>
                  ) : null}
                </div>
                <div className="flex grow flex-col p-5">
                  <h3 className="text-base font-extrabold tracking-tight text-slate-900">{item.title}</h3>
                  {item.description ? (
                    <p className="mt-2 grow text-xs leading-relaxed text-slate-500">{item.description}</p>
                  ) : (
                    <span className="grow" />
                  )}
                  <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
                    {item.price ? (
                      <span className="text-lg font-black tracking-tight text-[var(--color-primary)]">
                        {item.price}
                      </span>
                    ) : (
                      <span />
                    )}
                    <button
                      type="button"
                      onClick={onBookAny}
                      className="whitespace-nowrap rounded-full bg-slate-900 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white transition-colors hover:bg-[var(--color-primary)]"
                    >
                      {t('rt.pack.cta')}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** FAQ accordion shown on the home page by the road trip design. */
function HomeFaq({ storefront }: HomeDesignProps) {
  const { t } = useLocale();
  const [open, setOpen] = useState<number | null>(0);
  const items = storefront && storefront.faq.length > 0
    ? storefront.faq.map((row) => ({ q: row.question, a: row.answer }))
    : FAQS;

  return (
    <section className="bg-slate-50 py-14 lg:py-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-8">
        <div className="mb-8 text-center">
          <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
            {t('rt.faq.kicker')}
          </span>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t('rt.faq.title')}
          </h2>
        </div>
        <div className="space-y-3">
          {items.map((item, index) => {
            const isOpen = open === index;
            return (
              <div key={index} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : index)}
                  className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="text-sm font-bold text-slate-900">{item.q}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-[var(--color-primary)] transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen ? (
                  <p className="border-t border-slate-100 px-5 py-4 text-xs leading-relaxed text-slate-600">
                    {item.a}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Dark closing band: phone + WhatsApp handoff. */
function AppBand() {
  const { t } = useLocale();
  const brand = usePublicBrand();
  const phone = brand.phone.replace(/\s/g, '');
  const whatsapp = brand.whatsapp.replace(/\s/g, '');

  return (
    <section className="bg-[var(--color-secondary)] py-14 lg:py-20">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-8 lg:grid-cols-2">
        <div>
          <span className="mb-2 block text-xs font-black uppercase tracking-[0.2em] text-[var(--color-primary)]">
            {t('rt.app.kicker')}
          </span>
          <h2 className="text-2xl font-extrabold leading-tight tracking-tight text-white sm:text-3xl">
            {t('rt.app.title')}
          </h2>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-300">{t('rt.app.sub')}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={phone ? `tel:${phone}` : undefined}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)] px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-[var(--color-primary-dark)]"
            >
              <Phone className="h-4 w-4" />
              {brand.phone || t('rt.app.call')}
            </a>
            <a
              href={whatsapp ? `https://wa.me/${whatsapp}` : undefined}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-3 text-xs font-bold uppercase tracking-wider text-white transition-colors hover:border-[var(--color-primary)]"
            >
              <MessageCircle className="h-4 w-4" />
              {t('rt.app.whatsapp')}
            </a>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          {['rt.fleet.title', 'rt.safety.title', 'rt.pack.title'].map((key, index) => (
            <div
              key={key}
              className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center backdrop-blur-sm"
            >
              <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[var(--color-primary)]/15 text-[var(--color-primary)]">
                {index === 0 ? (
                  <CarFront className="h-5 w-5" />
                ) : index === 1 ? (
                  <ShieldCheck className="h-5 w-5" />
                ) : (
                  <Sparkles className="h-5 w-5" />
                )}
              </span>
              <span className="mt-3 block text-[11px] font-semibold leading-snug text-slate-200">
                {t(key)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
